import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import jobsHelper from "../../../../API/Jobs/jobsHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import JobSettingsView from "../SubComponents/JobSettingsView";

const JobsSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onJobCreated,
      selectedId,
      setShowJobModal,
      displayTokenSection,
      setDisplayTokenSection,
      setInitialMapValues = () => {},
      onJobDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace, getPlanConfig } = useWorkspaces();
    const savingRef = useRef(false);

    const validationSchema = AppForms.yup.object().shape({
      name: AppForms.yup.string().required().label("Name").max(255),
      description: AppForms.yup
        .string()
        .nullable()
        .label("Description")
        .max(2048),
      type: AppForms.yup.string().nullable().label("Job Type"),
      forcedPrompt: AppForms.yup
        .string()
        .nullable()
        .label("Forced Prompt")
        .max(1024),
      agentID: AppForms.yup.string().required().label("Agent"),
      customPromptID: AppForms.yup
        .string()
        .nullable()
        .label("Prompt")
        .test(
          "prompt-required",
          "Either select a prompt or provide a forced prompt",
          function (value) {
            const { forcedPrompt } = this.parent;
            return (
              (value && value.trim() !== "") ||
              (forcedPrompt && forcedPrompt.trim() !== "")
            );
          }
        ),
      routineConfig: AppForms.yup
        .object()
        .nullable()
        .shape({
          maxPlannedActions: AppForms.yup
            .number()
            .nullable()
            .min(1)
            .max(20)
            .label("Max Planned Actions"),
        }),
      timezone: AppForms.yup.string().required().label("Timezone"),
      schedule: AppForms.yup.object().shape({
        frequency: AppForms.yup.string().required().label("Frequency"),
        interval: AppForms.yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (typeof originalValue === "object" && originalValue !== null) {
              return originalValue.id;
            }
            return originalValue === "" ? null : value;
          })
          .min(1)
          .test(
            "routine-min-interval",
            "Routine jobs require a minimum interval of 30 minutes",
            function (value) {
              const { type } =
                this.from && this.from[1] ? this.from[1].value : {};
              const { frequency } = this.parent;
              if (
                type === "ROUTINE" &&
                frequency === "MINUTES" &&
                value != null &&
                value < 30
              ) {
                return false;
              }
              return true;
            }
          )
          .label("Interval"),
        cronExpression: AppForms.yup
          .string()
          .nullable()
          .label("Cron Expression"),
        dayOfWeek: AppForms.yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (typeof originalValue === "object" && originalValue !== null) {
              return originalValue.id;
            }
            return originalValue === "" ? null : value;
          })
          .min(0)
          .max(6)
          .label("Day of Week"),
        dayOfMonth: AppForms.yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (typeof originalValue === "object" && originalValue !== null) {
              return originalValue.id;
            }
            return originalValue === "" ? null : value;
          })
          .when("$dayOfMonth", {
            is: (val) => val != null,
            then: (schema) => schema.min(1).max(31),
            otherwise: (schema) => schema,
          })
          .label("Day of Month"),
        hour: AppForms.yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (typeof originalValue === "object" && originalValue !== null) {
              return originalValue.id;
            }
            return originalValue === "" ? null : value;
          })
          .min(0)
          .max(23)
          .label("Hour"),
        minute: AppForms.yup
          .number()
          .nullable()
          .transform((value, originalValue) => {
            if (typeof originalValue === "object" && originalValue !== null) {
              return originalValue.id;
            }
            return originalValue === "" ? null : value;
          })
          .min(0)
          .max(59)
          .label("Minute"),
      }),
      startDate: AppForms.yup.string().nullable().label(t("common.startDate")),
      endDate: AppForms.yup.string().nullable().label(t("common.endDate")),
      isActive: AppForms.yup.boolean().nullable().label("Active"),
      executionConfig: AppForms.yup
        .object()
        .nullable()
        .shape({
          timeout: AppForms.yup.number().nullable().min(1).label("Timeout"),
          maxDuration: AppForms.yup
            .number()
            .nullable()
            .min(1)
            .label("Max Duration"),
          priority: AppForms.yup.string().nullable().label("Priority"),
          inputParameters: AppForms.yup
            .object()
            .nullable()
            .label("Input Parameters"),
          outputFormat: AppForms.yup.string().nullable().label("Output Format"),
          webhookUrl: AppForms.yup
            .string()
            .nullable()
            .url()
            .label("Webhook URL"),
          webhookHeaders: AppForms.yup
            .object()
            .nullable()
            .label("Webhook Headers"),
        }),
      notificationConfig: AppForms.yup
        .object()
        .nullable()
        .shape({
          onSuccess: AppForms.yup
            .boolean()
            .nullable()
            .label("Notify on Success"),
          onFailure: AppForms.yup
            .boolean()
            .nullable()
            .label("Notify on Failure"),
          onStart: AppForms.yup.boolean().nullable().label("Notify on Start"),
          emailRecipients: AppForms.yup
            .array()
            .nullable()
            .of(AppForms.yup.string().email())
            .label("Email Recipients"),
          communicationServiceID: AppForms.yup
            .string()
            .nullable()
            .label("Communication Service"),
        }),
      retryConfig: AppForms.yup
        .object()
        .nullable()
        .shape({
          maxRetries: AppForms.yup
            .number()
            .nullable()
            .min(0)
            .max(10)
            .label("Max Retries"),
          retryInterval: AppForms.yup
            .number()
            .nullable()
            .min(1)
            .label("Retry Interval"),
          backoffMultiplier: AppForms.yup
            .number()
            .nullable()
            .min(1)
            .label("Backoff Multiplier"),
          maxRetryInterval: AppForms.yup
            .number()
            .nullable()
            .min(1)
            .label("Max Retry Interval"),
        }),
    });

    const handleDeletion = async (id) => {
      try {
        const result = await jobsHelper.deleteJob(id);

        // Refresh jobs list after deletion
        const workspaceID = getActiveWorkspace()?.id;
        if (workspaceID) {
          let freshJobs = await jobsHelper?.getJobsByWorkspaceID(workspaceID);

          freshJobs = freshJobs?.data?.cronJobsByWorkspace?.items?.filter(
            (u) => u?._deleted != true
          );
          dispatch("SET_JOBS", freshJobs || []);
        }

        AppToasty.show(t("toasts.jobDeleted"), {
          type: "success",
        });
        setShowJobModal?.(false);
        onJobDeleted();
      } catch (error) {
        console.error("jobDeleteErrorCaption", error);
        AppToasty.show(t("toasts.jobDeletionFailed"), {
          type: "danger",
        });
      }
    };

    const identifyOptByID = (id) => {
      return state.jobs.find((u) => u?.id === id);
    };

    const effectiveInitialValues = useMemo(() => {
      if (isEdit && selectedId) {
        const job = identifyOptByID(selectedId);
        if (job) {
          const parsed = { ...job };
          if (typeof parsed?.schedule === "string") {
            try {
              parsed.schedule = JSON.parse(parsed.schedule);
            } catch (e) {}
          }
          return parsed;
        }
      }
      return initialMapValues;
    }, [
      isEdit,
      selectedId,
      JSON.stringify(identifyOptByID(selectedId)),
      JSON.stringify(initialMapValues),
    ]);

    const calculateNextRunAt = (schedule, startDate, endDate) => {
      const now = new Date();
      let nextRun = new Date();

      if (startDate && new Date(startDate) > now) {
        nextRun = new Date(startDate);
      }

      // Set time based on schedule
      if (schedule.hour !== undefined) {
        nextRun.setHours(schedule.hour);
      }
      if (schedule.minute !== undefined) {
        nextRun.setMinutes(schedule.minute);
      }
      nextRun.setSeconds(0);
      nextRun.setMilliseconds(0);

      // Adjust based on frequency
      switch (schedule.frequency) {
        case "MINUTES":
          const minuteInterval =
            (typeof schedule.interval === "object"
              ? schedule.interval?.id
              : schedule.interval) || 1;
          nextRun = new Date(
            Math.ceil(now.getTime() / (minuteInterval * 60000)) *
              (minuteInterval * 60000)
          );
          break;
        case "HOURLY":
          const hourInterval =
            (typeof schedule.interval === "object"
              ? schedule.interval?.id
              : schedule.interval) || 1;
          nextRun = new Date(now.getTime() + hourInterval * 3600000);
          break;
        case "DAILY":
          if (nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
          break;
        case "WEEKLY":
          // dayOfWeek is stored Monday=0 ... Sunday=6 (backend convention);
          // convert JS getDay() (Sunday=0) to the same scale
          const targetDay = schedule.dayOfWeek || 0;
          const currentDay = (nextRun.getDay() + 6) % 7;
          let daysToAdd = targetDay - currentDay;
          if (daysToAdd < 0 || (daysToAdd === 0 && nextRun <= now)) {
            daysToAdd += 7;
          }
          nextRun.setDate(nextRun.getDate() + daysToAdd);
          break;
        case "MONTHLY":
          const targetDate = schedule.dayOfMonth || 1;
          nextRun.setDate(targetDate);
          if (nextRun <= now) {
            nextRun.setMonth(nextRun.getMonth() + 1);
          }
          break;
        case "YEARLY":
          if (nextRun <= now) {
            nextRun.setFullYear(nextRun.getFullYear() + 1);
          }
          break;
      }

      return nextRun.toISOString();
    };

    const handleJobItemSave = async (submitted) => {
      if (savingRef.current) return;
      savingRef.current = true;
      let v = { ...submitted };
      if (
        v.type === "ROUTINE" &&
        v.schedule?.frequency === "MINUTES" &&
        (!v.schedule.interval || v.schedule.interval < 30)
      ) {
        v = {
          ...v,
          schedule: {
            ...v.schedule,
            interval: 30,
          },
        };
      }
      let newJobs = state.jobs?.length > 0 ? [...state.jobs] : [];

      try {
        let result;

        if (isEdit) {
          // For updates, we can update these fields
          const nextRunAt = calculateNextRunAt(
            v.schedule,
            v.startDate,
            v.endDate
          );
          const status = v.isActive ? "ACTIVE" : "PAUSED";

          // Create a clean schedule object without timezone
          const { timezone, ...scheduleWithoutTimezone } = v.schedule;

          const updatePayload = [
            { property: "name", value: v.name },
            { property: "description", value: v.description },
            { property: "type", value: v.type || "TASK" },
            { property: "forcedPrompt", value: v.forcedPrompt },
            { property: "agentID", value: v.agentID },
            { property: "customPromptID", value: v.customPromptID || null },
            {
              property: "routineConfig",
              value:
                v.type === "ROUTINE"
                  ? {
                      maxPlannedActions:
                        v.routineConfig?.maxPlannedActions || 5,
                    }
                  : null,
            },
            { property: "schedule", value: scheduleWithoutTimezone },
            { property: "isActive", value: v.isActive },
            { property: "status", value: status },
            { property: "nextRunAt", value: nextRunAt },
            { property: "executionConfig", value: v.executionConfig },
            { property: "notificationConfig", value: v.notificationConfig },
            { property: "retryConfig", value: v.retryConfig },
            { property: "timezone", value: v.timezone },
            { property: "startDate", value: v.startDate },
            { property: "endDate", value: v.endDate },
          ];

          console.log(
            "Update payload:",
            JSON.stringify(updatePayload, null, 2)
          );
          console.log(
            "Original schedule object:",
            JSON.stringify(v.schedule, null, 2)
          );
          console.log(
            "Schedule without timezone:",
            JSON.stringify(scheduleWithoutTimezone, null, 2)
          );
          console.log(
            "Full values object before update:",
            JSON.stringify(v, null, 2)
          );
          console.log("Attempting to update job with ID:", selectedId);

          result = await jobsHelper.updateJob(selectedId, updatePayload);
          result = result?.data?.updateCronJob;
          const _updatedObject = identifyOptByID(result?.id);
          const updateIndex = newJobs.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newJobs[updateIndex] = result;
        } else {
          // Calculate nextRunAt and status for new jobs
          const nextRunAt = calculateNextRunAt(
            v.schedule,
            v.startDate,
            v.endDate
          );
          const status = v.isActive ? "ACTIVE" : "PAUSED";

          // Create the job with all required fields
          const createPayload = {
            name: v.name,
            description: v.description,
            type: v.type || "TASK",
            forcedPrompt: v.forcedPrompt,
            agentID: v.agentID,
            customPromptID: v.customPromptID,
            ...(v.type === "ROUTINE" && {
              routineConfig: {
                maxPlannedActions: v.routineConfig?.maxPlannedActions || 5,
              },
            }),
            schedule: {
              frequency: v.schedule.frequency,
              interval: v.schedule.interval || null,
              cronExpression: v.schedule.cronExpression || null,
              dayOfWeek:
                v.schedule.dayOfWeek !== undefined
                  ? v.schedule.dayOfWeek
                  : null,
              dayOfMonth:
                v.schedule.dayOfMonth !== undefined
                  ? v.schedule.dayOfMonth
                  : null,
              hour: v.schedule.hour !== undefined ? v.schedule.hour : null,
              minute:
                v.schedule.minute !== undefined ? v.schedule.minute : null,
            },
            timezone: v.timezone,
            isActive: v.isActive,
            status: status,
            nextRunAt: nextRunAt,
            startDate: v.startDate || null,
            endDate: v.endDate || null,
            workspaceID: getActiveWorkspace()?.id,
            // Only include optional configs if they have values
            ...(v.executionConfig && {
              executionConfig: {
                timeout: v.executionConfig.timeout || null,
                maxDuration: v.executionConfig.maxDuration || null,
                priority: v.executionConfig.priority || null,
                inputParameters: v.executionConfig.inputParameters || null,
                outputFormat: v.executionConfig.outputFormat || null,
                webhookUrl: v.executionConfig.webhookUrl || null,
                webhookHeaders: v.executionConfig.webhookHeaders || null,
              },
            }),
            ...(v.notificationConfig && {
              notificationConfig: {
                onSuccess: v.notificationConfig.onSuccess || false,
                onFailure: v.notificationConfig.onFailure || false,
                onStart: v.notificationConfig.onStart || false,
                emailRecipients: v.notificationConfig.emailRecipients || null,
                communicationServiceID:
                  v.notificationConfig.communicationServiceID || null,
              },
            }),
            ...(v.retryConfig && {
              retryConfig: {
                maxRetries:
                  v.retryConfig.maxRetries !== undefined
                    ? v.retryConfig.maxRetries
                    : null,
                retryInterval: v.retryConfig.retryInterval || null,
                backoffMultiplier: v.retryConfig.backoffMultiplier || null,
                maxRetryInterval: v.retryConfig.maxRetryInterval || null,
              },
            }),
          };

          console.log(
            "Create payload:",
            JSON.stringify(createPayload, null, 2)
          );
          console.log("Schedule object:", JSON.stringify(v.schedule, null, 2));
          console.log(
            "Full values object before create:",
            JSON.stringify(v, null, 2)
          );
          console.log(
            "Attempting to create new job in workspace:",
            getActiveWorkspace()?.id
          );

          result = await jobsHelper.createJob(createPayload);

          result = result?.data?.createCronJob;
          newJobs.push(result);
        }

        dispatch("SET_JOBS", [...newJobs]);
        if (!isEdit) {
          setInitialMapValues({
            ...result,
          });
        }
        // Refresh jobs list to ensure we have the latest data

        try {
          const workspaceID = getActiveWorkspace()?.id;
          if (workspaceID) {
            let freshJobs = await jobsHelper?.getJobsByWorkspaceID(workspaceID);

            freshJobs = freshJobs?.data?.cronJobsByWorkspace?.items?.filter(
              (u) => u?._deleted != true
            );
            dispatch("SET_JOBS", freshJobs || []);
          }
        } catch (refreshError) {
          console.error("Error refreshing jobs:", refreshError);
        }

        setShowJobModal?.(false);
        backTo && navigateBack();
        const savedId = result?.id;
        onJobCreated && onJobCreated("success", isEdit, savedId, submitted);
      } catch (error) {
        console.error(error);
        onJobCreated && onJobCreated("error", isEdit, null, submitted);
      } finally {
        savingRef.current = false;
        setShowJobModal?.(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Job : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={effectiveInitialValues}
            onSubmit={handleJobItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <JobSettingsView
              setDisplayTokenSection={setDisplayTokenSection}
              displayTokenSection={displayTokenSection}
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        effectiveInitialValues,
        JSON.stringify(state.jobs),
        isEdit,
        selectedId,
        displayTokenSection,
        AppToasty,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default JobsSettings;
